// @atlas/contracts — a `employees` modul Zod-sémái
//
// A fájl CSAK sémákat tart (érték-szintű Zod-objektumok + a belőlük inferált típusok) — a ROUTE-ok
// egyetlen `atlasContract` objektum-literálban maradnak (`src/index.ts`), mert a ts-rest
// kontextus-inferenciája a lapos bővítésre érzékeny. A sémák mozgatása ezt NEM érinti: sima értékek.
import { z } from 'zod';
import { isoDateStr, MAX_CHILDREN, PersonChildSchema } from '../_shared/schemas.js';
/** Foglalkoztatási forma (HR-kör #43): alkalmazott (alapértelmezett) vagy vállalkozó. */
export const EMPLOYMENT_TYPES = ['employee', 'contractor'];
/** A munkáltató cég — a számlázási kiállítók kulcsai: MORGENS Design Kft. (alapértelmezett) / MORGENS Digital Kft. */
export const EMPLOYER_KEYS = ['MOR', 'MDIG'];
/**
 * Munkatárs-sor a belső címtárhoz: a platform `identity` (type=internal) + a hozzárendelt Atlas-szerepek
 * (RBAC). A `roles` best-effort: csak akkor telik meg, ha a hívónak van szerep-olvasási joga (atlas:admin /
 * platform:admin); jog nélkül a címtár enélkül is renderel (a szerep-oszlop üres marad). A HR-adat
 * (beosztás/részleg) MÉG NINCS betöltve — a v1 az identity-alapokra épül, később bővíthető.
 */
export const EmployeeRowSchema = z.object({
    id: z.string(),
    email: z.string().nullable(),
    displayName: z.string().nullable(),
    /** OIDC név-részek — a locale-tudatos megjelenítéshez (HU: vezetéknév elöl). A `displayName` a fallback. */
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    /** Presigned avatar-URL (objektum-tár), ha van profilkép — különben null (monogram fallback). */
    avatarUrl: z.string().nullable(),
    /** Beosztás (pozíció) — a workforce `positions` lokalizált neve (a `lang` szerint), vagy null. */
    position: z.string().nullable(),
    /**
     * Becenév (a `platform.identity_profiles.nickname`-ből) — a szerkesztő előtöltéséhez (az Odin
     * `listIdentities` projektálja). A szerkesztő ettől függetlenül dirty-alapon küldi.
     */
    nickname: z.string().nullable(),
    /** Telefonszám (a `platform.identity_profiles.phone`-ból — ugyanaz a mező, amit a Profilom ír). */
    phone: z.string().nullable(),
    /**
     * Csapat — a workforce `teams` lokalizált neve (a `lang` szerint), vagy null. A beosztás→csapat
     * láncból származik, NEM önálló mező a munkatárson.
     */
    team: z.string().nullable(),
    /** A CSAPAT szín-tokenje (MORGENS-paletta: grove/coral/… — az avatar-háttérhez/gyűrűhöz), vagy null. */
    teamColor: z.string().nullable(),
    /**
     * Részleg — a csapat RÉSZLEGÉNEK lokalizált neve (beosztás→csapat→részleg, két szint feloldva), vagy
     * null. A részlegnek NINCS színe: a lista a csapat színét használja.
     */
    department: z.string().nullable(),
    /** Az illetőhöz rendelt eszközök (workforce/assets, holder=identity) — hardware elöl, majd software. */
    assets: z.array(z.object({ id: z.string(), name: z.string(), category: z.string() })),
    /**
     * A SUPPORT-CSAPATOK, amelyeknek a kolléga tagja (a legacy „Ügyfélszolgálat" oszlopa) — CSAK megjelenítés.
     * A tagságot a Support-csapatok oldalon szerkesztjük (egy szerkesztő-felület), itt olvasható jelzés.
     */
    supportTeams: z.array(z.object({ id: z.string(), name: z.string() })),
    status: z.string(),
    /**
     * KIVEZETVE (offboardolva) — ISO időbélyeg, vagy `null` az élő soron.
     *
     * A `status` és az ÉLETCIKLUS két külön tengely: a státusz üzleti állapot (aktív/felfüggesztett/archivált),
     * az életciklus azt mondja meg, hogy a személy a címtárban ÉL-e. Ma a kivezetés mindkettőt állítja, de a
     * felület az életciklusra kapuz (kivezetett soron nincs szerkesztés/offboarding), hogy a két fogalom
     * véletlen egybeesésére ne épüljön szabály.
     */
    offboardedAt: z.string().nullable().default(null),
    createdAt: z.string(),
    roles: z.array(z.string()),
    // ── Munkaviszony-adat a `workforce/employee-details` [T] sorból (best-effort a listában: ha a sor még
    //    nem tölthető be, a mezők default-üresek maradnak — a szerkesztő OCC-je akkor 428/409-cel véd). ──
    /** A pozíció FK-ja (`workforce/positions`) — a szerkesztő SelectField előtöltéséhez; nincs sor → null. */
    positionId: z.string().nullable(),
    /**
     * HR-DÁTUMOK a `workforce/employee-details` sorból (mindkettő OPCIONÁLIS, `YYYY-MM-DD` NAP-pontos):
     * születési dátum és munkába lépés dátuma. Nap-pontos (nem instant), ezért nincs zóna-konverzió — egy
     * születésnap nem csúszhat el UTC↔Budapest váltáskor. A legacy CRM ezeket nem tartotta: a mező üresen indul.
     */
    birthDate: z.string().nullable(),
    employmentStartDate: z.string().nullable(),
    /** Kilépés napja (`YYYY-MM-DD`) — ha a kolléga elmegy (pl. határozott idejű szerződés vége); HR-mező. */
    employmentEndDate: z.string().nullable().default(null),
    /** Névnap (`YYYY-MM-DD`) — SZEMÉLYES adat: a dolgozó a belső Profilomon maga is írhatja. */
    nameDay: z.string().nullable().default(null),
    /** A dolgozó gyerekei (teljes név + születési dátum) — SZEMÉLYES adat, mint a névnap. */
    children: z.array(PersonChildSchema).default([]),
    /** Foglalkoztatási forma (#43) — a régi sorokon az Odin alapértéke (`employee`). */
    employmentType: z.enum(EMPLOYMENT_TYPES).default('employee'),
    /** A munkáltató cég kulcsa (#43) — a régi sorokon az Odin alapértéke (`MOR`). */
    employerKey: z.enum(EMPLOYER_KEYS).default('MOR'),
    /** Próbaidős-e (#111) — a régi sorokon az Odin alapértéke (`false`). */
    probation: z.boolean().default(false),
    /** A próbaidő kezdete és lejárata (`YYYY-MM-DD`, opcionális) — csak bekapcsolt próbaidővel lehet értékük. */
    probationStartDate: z.string().nullable().default(null),
    probationEndDate: z.string().nullable().default(null),
    /** Az `employee-details` sor OCC-verziója (a szerkesztő If-Match-ének forrása); nincs sor → 0. */
    version: z.number(),
});
/**
 * A próbaidő (#111) két szabálya — UGYANAZ, mint az Odin két CHECK-je, csak itt mezőre vezetett 422-vel: (1)
 * kikapcsolt próbaidő mellé nem jöhet dátum (a BFF kikapcsoláskor maga üríti a dátumokat); (2) a lejárat nem lehet a
 * kezdet előtt. Parciális body: a hiányzó mezőt nem ítéli meg (a sorral való ütközést az Odin CHECK-je fogja meg). Az ok a
 * `params.reason`-ben (nem `message`-ben), hogy a form error-map-je lokalizálhassa
 */
function refineProbation(v, ctx) {
    if (v.probation === false) {
        for (const key of ['probationStartDate', 'probationEndDate']) {
            if (typeof v[key] === 'string') {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [key],
                    params: { reason: 'probation_off' },
                });
            }
        }
    }
    if (typeof v.probationStartDate === 'string' &&
        typeof v.probationEndDate === 'string' &&
        v.probationEndDate < v.probationStartDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['probationEndDate'],
            params: { reason: 'probation_end_before_start' },
        });
    }
}
/**
 * A „Munkatárs szerkesztése" write-body: a `workforce/employee-details` [T] sor SZERKESZTHETŐ, fehérlistás
 * mezői (`positionId`, dátumok) + az identity-mag (e-mail/név) PARCIÁLIS PATCH-e. Az identity-magot az
 * Odin `PATCH /v1/admin/identities/:id` írja (e-mail-ütközés → 409 → az e-mail mezőre vezetett hiba); a
 * support-csapat-tagságokat MÁR NEM ez kezeli (kizárólag a support-csapat-szerkesztő).
 * A `positionId` KÖTELEZŐ UUID (a beosztás mindenkinél kötelező — nincs „nincs beosztás").
 * A `discordId` SZÁNDÉKOSAN NINCS ebben a body-ban: a mező a PROFIL-lapon él (`profile.tx.ts`), a
 * munkatárs-űrlapról tudatosan kimaradt — az indoklás az `employees.tx.ts` fejlécében.
 */
export const EmployeeUpdateBodySchema = z
    .object({
    // Identity-mag (parciális PATCH — az EmployeeCreateBodySchema mintájára; az Odin a saját identityt kizárja
    // az egyediség-ellenőrzésből → a változatlan e-mail no-op, nincs ál-409).
    email: z.string().trim().email().max(320).optional(),
    // a NÉV szerkesztéskor sem üríthető (user-döntés): ha jön, nem üres — `null` (törlés) sincs
    givenName: z.string().trim().min(1).max(255).optional(),
    familyName: z.string().trim().min(1).max(255).optional(),
    displayName: z.string().trim().max(255).nullable().optional(),
    // Becenév (platform.identity_profiles.nickname) — parciális: string=beállítás, null=törlés, undefined=nincs
    // változás. A form dirty-alapon küldi (érintetlen mező → undefined → nem nulláz némán meglévő értéket).
    nickname: z.string().trim().max(255).nullable().optional(),
    // Telefon (platform.identity_profiles.phone — a Profilommal KÖZÖS mező): string=beállítás, null=törlés,
    // undefined=nincs változás. A kapcsolattartó-body-val azonos határ (max 64).
    phone: z.string().trim().max(64).nullable().optional(),
    // A beosztás KÖTELEZŐ (nincs „nincs beosztás" opció) — a form required select-tel kényszeríti ki.
    positionId: z.string().uuid(),
    // HR-DÁTUMOK (opcionális, `YYYY-MM-DD`): parciális PATCH-szemantika — string=beállítás, null=törlés,
    // undefined=nincs változás (a form dirty-alapon küldi, hogy egy érintetlen mező ne nullázzon némán).
    birthDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    employmentStartDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    // A #40 HR-kör mezői: kilépés napja + névnap (nap-pontos, parciális), gyerekek (a TELJES lista — a form mindig
    // a teljeset küldi; `undefined` = nincs változás).
    employmentEndDate: isoDateStr.nullable().optional(),
    nameDay: isoDateStr.nullable().optional(),
    children: z.array(PersonChildSchema).max(MAX_CHILDREN).optional(),
    // #43: foglalkoztatási forma + munkáltató (parciális; `undefined` = nincs változás)
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    employerKey: z.enum(EMPLOYER_KEYS).optional(),
    // #111: próbaidő-kapcsoló + a két opcionális dátum (parciális; a szabályt a `refineProbation` adja)
    probation: z.boolean().optional(),
    probationStartDate: isoDateStr.nullable().optional(),
    probationEndDate: isoDateStr.nullable().optional(),
})
    .superRefine(refineProbation);
/**
 * Az „Új munkatárs" write-body (IdP-provisioning): a szerkeszthető munkaviszony-mezők + az identity-mag
 * (e-mail KÖTELEZŐ, kulcs; a vezeték- és keresztnév is KÖTELEZŐ) + az Atlas default szerep. Match esetén (létező e-mail) az Odin a
 * megosztott core-t NEM írja felül (A döntés). A `roleKey` a tier-modell whitelistje (default a szerveren: member).
 */
export const EmployeeCreateBodySchema = z
    .object({
    email: z.string().trim().email().max(320),
    // a NÉV mindkét része KÖTELEZŐ (user-döntés) — üres stringet sem fogadunk el
    givenName: z.string().trim().min(1).max(255),
    familyName: z.string().trim().min(1).max(255),
    displayName: z.string().trim().max(255).nullable().optional(),
    // Becenév (platform.identity_profiles.nickname) — opcionális a felvételkor is (üres → nem íródik profil-mező).
    nickname: z.string().trim().max(255).nullable().optional(),
    // Telefon (platform.identity_profiles.phone) — opcionális a felvételkor is.
    phone: z.string().trim().max(64).nullable().optional(),
    // A beosztás KÖTELEZŐ (nincs „nincs beosztás" opció) — a felvételkor is meg kell adni.
    positionId: z.string().uuid(),
    /**
     * KEZDŐ JELSZÓ (opcionális, write-only): megadva a felvett személy AZONNAL be tud lépni; üresen hagyva a
     * belépést a köszöntő-levél egyszer használatos, lejáró LINKJE adja (a biztonságosabb, alapértelmezett út).
     * A TARTALMI policy (min. 12 karakter, kis+nagy+szám) EGYETLEN autoritása az Odin — itt csak a durva
     * hossz-korlát van (a 422 mezőnkénti policy-kódokat ad, amiket a UI lokalizál). SOHA nem tér vissza olvasáskor.
     */
    password: z.string().min(1).max(200).optional(),
    // HR-DÁTUMOK (opcionális, `YYYY-MM-DD`): parciális PATCH-szemantika — string=beállítás, null=törlés,
    // undefined=nincs változás (a form dirty-alapon küldi, hogy egy érintetlen mező ne nullázzon némán).
    birthDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    employmentStartDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    // A #40 HR-kör mezői: kilépés napja + névnap (nap-pontos, parciális), gyerekek (a TELJES lista — a form mindig
    // a teljeset küldi; `undefined` = nincs változás).
    employmentEndDate: isoDateStr.nullable().optional(),
    nameDay: isoDateStr.nullable().optional(),
    children: z.array(PersonChildSchema).max(MAX_CHILDREN).optional(),
    // #43: foglalkoztatási forma + munkáltató (parciális; `undefined` = nincs változás)
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    employerKey: z.enum(EMPLOYER_KEYS).optional(),
    // #111: próbaidő-kapcsoló + a két opcionális dátum (parciális; a szabályt a `refineProbation` adja)
    probation: z.boolean().optional(),
    probationStartDate: isoDateStr.nullable().optional(),
    probationEndDate: isoDateStr.nullable().optional(),
    // A felvétel TISZTA GLOBÁLIS provisioning (IdP-modell): a személy + workforce-adat jön létre, app-tagság/szerep
    // NEM — azt a Jogosultságok-oldal adja apponként. Ezért itt NINCS roleKey/app (korábban volt — eltávolítva).
    // A support-csapat-tagságokat a support-csapat-szerkesztő kezeli (nem a munkatárs-form).
})
    .superRefine(refineProbation);
/**
 * A „Munkatárs szerkesztése" opció-listái: pozíciók (kis referencialista → select).
 *
 * A `teamName` NEM dísz: a beosztás-név csak CSAPATON BELÜL egyedi (a fa szerint egy szerep több csapatban is
 * élhet — `org-structure.tx.ts` `inTeam`), tehát a puszta név MELLÉ kell a csapat, különben a választóban két
 * azonos címke állna egymás mellett, és a választás találgatás lenne. Feloldatlan csapat → `null` (a select
 * ilyenkor a csupasz nevet mutatja, nem egy üres zárójelet).
 */
export const EmployeeRefsSchema = z.object({
    positions: z.array(z.object({ id: z.string(), name: z.string(), teamName: z.string().nullable() })),
});
//# sourceMappingURL=schemas.js.map